'use client';

import {
	CustomButton,
	IconButton,
	RoundButton,
	SecondaryButtonMT,
} from '@/components/buttons/Buttons';
import TabViewContainer from '@/components/tabs/TabViewContainer';
import routes from '@/constants/routes';
import ConfirmationDialogHeader from '@/modules/confirmation/components/ConfirmationDialogHeader';
import { showConfirmationDialog } from '@/modules/confirmation/engines/confirmation.library';
import { showNotification } from '@/modules/notification/engine/notification.library';
import { useNavigate } from 'react-router-dom';
import { Menu } from 'primereact/menu';
import type { MenuItem } from 'primereact/menuitem';
import { classNames } from 'primereact/utils';
import { type ReactNode, useRef } from 'react';
import { FaCar, FaClock } from 'react-icons/fa';
import {
	MdArrowBack,
	MdMoreVert,
	MdPinDrop,
	MdReceiptLong,
} from 'react-icons/md';
import PeopleTable from '../components/PeopleTable';
import Rules from '../components/Rules';
import style from './program.module.css';
import { useAuthenticationStore } from '@/store';
import { X4bUserRoleEnum } from '@/constants/roles';

type Group = {
	name: string;
	description: string;
	peopleCount: number;
};

type Rule = {
	icon: ReactNode;
	title: string;
	value: string;
	onClick?: () => void;
};

interface Program {
	name: string;
	description: string;
	rules: Rule[];
	groups: Group[];
}

const ViewProgramScreen = () => {
	const navigate1 = useNavigate();
	const isManager = useAuthenticationStore((state) => state.hasRoles)([X4bUserRoleEnum.Manager]);

	const menu = useRef<Menu>(null);

	const items: MenuItem[] = [
		{
			label: 'Delete Program',
			command: () => handleDeleteProgram(),
		},
	];

	const navigate = () => {
		navigate1(routes['/programs']);
	};

	const handleEditProgram = () => {
		navigate1(routes['/programs/edit']);
	};

	const deleteHeaderTitle = 'Delete program?';

	const deleteMessage =
		'Are you sure you want to delete this program? This action cannot be undone';

	const deleteHeaderTemplate = (
		<ConfirmationDialogHeader
			message={deleteHeaderTitle}
			severity="warning"
		/>
	);

	const handleDeleteProgram = () =>
		showConfirmationDialog(deleteMessage, {
			accept: () => {
				navigate();
				showNotification('Program successfully deleted.');
			},
			acceptLabel: 'Delete Program',
			rejectLabel: 'Cancel',
			header: deleteHeaderTemplate,
			acceptClassName: '!bg-error',
		});

	const rules: Rule[] = [
		{
			icon: <MdReceiptLong className="text-primary" size={24} />,
			title: 'Full coverage of trip expenses',
			value: 'Expense code required',
		},
		{
			icon: <MdPinDrop className="text-primary" size={24} />,
			title: 'Locations',
			value: 'Xpress Brixton (#3 Brixton building, Three Brixton st. Brgy. Kapitolyo, Pasig City, Metro Manila, Philippines) 3km radius',
		},
		{
			icon: <FaClock className="text-primary" size={24} />,
			title: 'Time',
			value: '08:00 am - 06:00 pm (10) hours [Mon, Tue, Wed, Thu, Fri]',
		},
		{
			icon: <FaCar className="text-primary" size={24} />,
			title: 'Ride type',
			value: 'Limited ride types (4 selected)',
		},
	];

	const groups: Group[] = [
		{
			name: 'Product Team',
			description: 'Design, BAs, and QAs',
			peopleCount: 5,
		},
	];

	const program: Program = {
		name: 'Business Travel',
		description:
			'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
		rules: rules,
		groups: groups,
	};

	const tabs = [
		{
			tabName: 'Rules',
			tabContent: (
				<div className="mt-2">
					<Rules rules={program.rules} />
				</div>
			),
		},
		{
			tabName: 'People',
			tabContent: (
				<div className="mt-2">
					<PeopleTable groups={program.groups} />
				</div>
			),
		},
	];

	return (
		<div>
			<div className="flex items-center mb-5">
				<IconButton
					id="back-btn"
					icon={
						<MdArrowBack size={24} className="text-primary mr-2" />
					}
					label="Back"
					className={style.programBackButton}
					onClick={navigate}
				/>
			</div>
			<div className="space-y-6">
				<div className="space-y-3">
					<div className="flex justify-between items-center">
						<div className="flex items-center space-x-2.5">
							<h1 className="text-headline-xx-large text-primary">
								{program.name}
							</h1>
							{isManager && (
								<CustomButton
									id="edit-program"
									aria-controls="edit-program"
									aria-haspopup="true"
									rounded
									className="p-3 hover:bg-surfaceContainerHighest"
									onClick={(event) => menu.current?.toggle(event)}
								>
									<MdMoreVert
										size={24}
										className="text-primary"
									/>
								</CustomButton>
							)}
							<Menu
								model={items}
								popup
								ref={menu}
								id="delete-program"
								pt={{
									root: {
										className: classNames(
											style.programMenu,
										),
									},
									content: {
										className: classNames(
											style.programMenu,
										),
									},
								}}
							/>
						</div>
						{isManager && (
							<SecondaryButtonMT
								id={'edit-program'}
								label="Edit Program"
								onClick={handleEditProgram}
							/>)}
					</div>
					<div>
						<p className="text-title-medium text-xl">
							{program.description}
						</p>
					</div>
				</div>
				<div>
					<TabViewContainer tabList={tabs} />
				</div>
			</div>
		</div>
	);
};

export default ViewProgramScreen;
